// ITServices.js

import React, { useEffect } from "react";
import { Container, Box, Typography, Grid, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import DevicesIcon from "@mui/icons-material/Devices";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import SecurityIcon from "@mui/icons-material/Security";
import BugReportIcon from "@mui/icons-material/BugReport";

export default function ITServices() {
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const capabilities = [
    {
      icon: <DevicesIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Web & Mobile Development",
      description:
        "Modern, responsive, and performant apps built using Flutter, React, Node, and cloud‑native backends.",
    },
    {
      icon: <CloudQueueIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Cloud & DevOps",
      description:
        "Scalable deployments, CI/CD pipelines, infra automation, and cost‑optimized cloud architectures on AWS, GCP, and Azure.",
    },
    {
      icon: <DesignServicesIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "UI/UX & Frontend Engineering",
      description:
        "Pixel‑perfect interfaces with consistent design systems, smooth interactions, and a premium, trustworthy user experience.",
    },
    {
      icon: <BugReportIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "QA & Automation",
      description:
        "End‑to‑end testing, regression suites, automation frameworks, and release pipelines that maintain product stability.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Security‑First Engineering",
      description:
        "Best practices in auth, access control, data protection, and secure communication baked into every layer of your stack.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        color: "white",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={6} alignItems="center">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "3.3rem" },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  Full‑stack <Box component="span" sx={{ color: PRIMARY_GREEN }}>IT Services</Box> for
                  modern products.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h6" sx={{ color: LIGHT_GRAY, fontWeight: 300, mb: 3 }}>
                  We design, build, and maintain production‑grade applications with the engineering
                  discipline of an enterprise and the speed of a startup.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, mb: 4, fontWeight: 300 }}>
                  From greenfield builds to refactoring existing systems, our team works as an
                  extension of your product and engineering org — bringing architecture, execution,
                  and reliability together.
                </Typography>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={{ display: "flex", gap: theme.spacing(2), flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/contact")}
                >
                  Start a Project
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/services")}
                >
                  View All Services
                </Button>
              </motion.div>
            </Grid>

            {/* RIGHT CONTENT: CAPABILITIES GRID */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {capabilities.map((item, index) => (
                  <Grid item xs={12} sm={6} key={item.title}>
                    <motion.div
                      variants={itemVariants}
                      style={{
                        background: "rgba(15, 23, 42, 0.9)",
                        borderRadius: 16,
                        padding: "18px 16px",
                        border: "1px solid rgba(148, 163, 184, 0.25)",
                        height: "100%",
                      }}
                    >
                      {item.icon}
                      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: LIGHT_GRAY, fontSize: 13 }}>
                        {item.description}
                      </Typography>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}